/**
 * Cart & Checkout Pages - Todo en Local Marketplace
 */

import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Trash2, Plus, Minus, ShoppingCart, Store, MapPin,
  Truck, Package, CreditCard, Banknote, ChevronRight, Check,
  Navigation, Loader2, AlertCircle, Move, Info, MapPinOff
} from 'lucide-react';
import { toast } from 'sonner';
import { getProductImage } from '../../utils/constants';
import api from '../../services/api';
import { getErrorMessage } from '../../utils/errorUtils';
import { MarketplaceHeader, MarketplaceFooter, MobileBottomNav } from '../../components/marketplace/MarketplaceLayout';

// Import Leaflet for delivery map
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom delivery marker icon
const deliveryIcon = new L.Icon({
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Brand colors - Professional & Modern
const COLORS = {
  navyBlue: '#1e3a5f',      // Azul marino profundo
  darkSlate: '#0f172a',     // Slate muy oscuro
  orange: '#F37322',        // Naranja - CTA principal
  lightGray: '#f8fafc',
  white: '#ffffff'
};

// ==================== DRAGGABLE MAP MARKER COMPONENT ====================
const DraggableMarker = ({ position, onPositionChange }) => {
  const markerRef = useRef(null);
  
  const eventHandlers = {
    dragend() {
      const marker = markerRef.current;
      if (marker != null) {
        const newPos = marker.getLatLng();
        onPositionChange({ lat: newPos.lat, lng: newPos.lng });
      }
    },
  };

  return (
    <Marker
      draggable={true}
      eventHandlers={eventHandlers}
      position={[position.lat, position.lng]}
      ref={markerRef}
      icon={deliveryIcon}
    />
  );
};

// ==================== MAP CLICK HANDLER COMPONENT ====================
const MapClickHandler = ({ onMapClick }) => {
  useMapEvents({
    click: (e) => {
      onMapClick({ lat: e.latlng.lat, lng: e.latlng.lng });
    },
  });
  return null;
};

// ==================== DELIVERY LOCATION MAP ====================
const DeliveryLocationMap = ({ coords, onCoordsChange }) => {
  const mapCenter = coords ? [coords.lat, coords.lng] : [19.4326, -99.1332];
  
  const handlePositionChange = async (newCoords) => {
    onCoordsChange(newCoords);
    
    // Reverse geocode to get address
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${newCoords.lat}&lon=${newCoords.lng}&addressdetails=1`
      );
      const data = await response.json();
      
      if (data.display_name) {
        // Return the address through the callback
        onCoordsChange(newCoords, data.display_name);
      }
    } catch (error) {
      console.log('Could not reverse geocode');
    }
  };

  return (
    <div className="relative rounded-lg overflow-hidden border-2 border-slate-200">
      <MapContainer
        center={mapCenter}
        zoom={16}
        style={{ height: '250px', width: '100%' }}
        scrollWheelZoom={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {coords && (
          <DraggableMarker 
            position={coords} 
            onPositionChange={handlePositionChange}
          />
        )}
        <MapClickHandler onMapClick={handlePositionChange} />
      </MapContainer>
      
      {/* Instructions overlay */}
      <div className="absolute bottom-2 left-2 right-2 bg-white/90 backdrop-blur-sm rounded-lg p-2 text-xs text-slate-600 flex items-center gap-2">
        <Move className="w-4 h-4 text-blue-500" />
        <span>Arrastra el marcador o toca el mapa para ajustar la ubicación</span>
      </div>
    </div>
  );
};

// ==================== CART PAGE ====================
export const CartPage = () => {
  const navigate = useNavigate();
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);

  const customerToken = localStorage.getItem('customer_token');

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!customerToken) {
      navigate('/auth?redirect=/carrito');
      return;
    }
    loadCart();
  }, [customerToken]);

  const loadCart = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/marketplace/cart?customer_token=${customerToken}`);
      setCart(response.data);
    } catch (error) {
      console.error('Error loading cart:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('customer_token');
        navigate('/auth?redirect=/carrito');
      }
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = async (productId, branchId, newQuantity) => {
    try {
      await api.put(`/marketplace/cart/update?customer_token=${customerToken}`, {
        product_id: productId,
        branch_id: branchId,
        quantity: newQuantity
      });
      loadCart();
      window.dispatchEvent(new Event('cart-updated'));
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al actualizar'));
    }
  };

  const removeItem = (productId, branchId) => {
    updateQuantity(productId, branchId, 0);
  };

  const clearCart = async () => {
    try {
      await api.delete(`/marketplace/cart/clear?customer_token=${customerToken}`);
      setCart({ items: [], total: 0 });
      toast.success('Carrito vacío');
      window.dispatchEvent(new Event('cart-updated'));
    } catch (error) {
      toast.error('Error al vaciar el carrito');
    }
  };

  // Group items by store
  const itemsByStore = cart.items.reduce((acc, item) => {
    if (!acc[item.business_id]) {
      acc[item.business_id] = {
        business_name: item.business_name,
        items: []
      };
    }
    acc[item.business_id].items.push(item);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <MarketplaceHeader />
        <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
          <div className="bg-white rounded-xl p-4 animate-pulse">
            <div className="h-5 bg-slate-200 rounded w-32 mb-4" />
            {[1,2].map(i => (
              <div key={i} className="flex gap-4 mb-4">
                <div className="w-20 h-20 bg-slate-200 rounded-lg" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-slate-200 rounded w-3/4" />
                  <div className="h-3 bg-slate-200 rounded w-1/2" />
                  <div className="h-5 bg-slate-200 rounded w-20" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <MarketplaceHeader />

      {/* Page title bar */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button onClick={() => navigate(-1)} className="p-2 hover:bg-slate-100 rounded-full sm:hidden">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="font-semibold text-lg">Mi Carrito</h1>
          </div>
          {cart.items.length > 0 && (
            <button onClick={clearCart} className="text-red-500 text-sm hover:underline">
              Vaciar
            </button>
          )}
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 py-6 flex-1 w-full">
        {cart.items.length === 0 ? (
          <div className="text-center py-16">
            <ShoppingCart className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-700 mb-2">Tu carrito está vacío</h2>
            <p className="text-slate-500 mb-6">Agrega productos para comenzar</p>
            <Link 
              to="/buscar"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-white font-medium"
              style={{ backgroundColor: COLORS.navyBlue }}
            >
              Explorar productos
            </Link>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Items */}
            <div className="lg:col-span-2 space-y-4">
              {Object.entries(itemsByStore).map(([businessId, store]) => (
                <div key={businessId} className="bg-white rounded-xl overflow-hidden">
                  <div className="px-4 py-3 bg-slate-50 border-b flex items-center gap-2">
                    <Store className="w-4 h-4 text-slate-500" />
                    <span className="font-medium text-slate-700">{store.business_name}</span>
                  </div>
                  <div className="divide-y">
                    {store.items.map((item) => (
                      <div key={`${item.product_id}-${item.branch_id}`} className="p-4 flex gap-4">
                        <div className="w-20 h-20 bg-slate-50 rounded-lg flex-shrink-0 overflow-hidden">
                          <img 
                            src={getProductImage(item.product_image)}
                            alt={item.product_name}
                            className="w-full h-full object-cover"
                            onError={(e) => { e.target.src = getProductImage(); }}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-slate-800 line-clamp-2">
                            {item.product_name}
                            {item.is_combo && <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-purple-100 text-purple-700">Combo</span>}
                            {item.source === 'comanda_pro' && !item.is_combo && <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-orange-100 text-orange-700">Restaurante</span>}
                          </h3>
                          <p className="text-sm text-slate-500">{item.branch_name}</p>
                          {item.modifier_selections?.length > 0 && (
                            <p className="text-xs text-slate-500 mt-1">
                              {item.modifier_selections.flatMap(g => (g.options || []).map(o =>
                                o.price_adjustment > 0 ? `${o.name} (+$${o.price_adjustment.toFixed(2)})` : o.name
                              )).join(' · ')}
                            </p>
                          )}
                          {item.combo_selections?.length > 0 && (
                            <p className="text-xs text-slate-500 mt-1">
                              {item.combo_selections.map(s =>
                                s.price_adjustment > 0 ? `${s.product_name} (+$${s.price_adjustment.toFixed(2)})` : s.product_name
                              ).join(' + ')}
                            </p>
                          )}
                          {item.special_instructions && (
                            <p className="text-xs text-amber-600 mt-1 italic">Nota: {item.special_instructions}</p>
                          )}
                          <p className="text-lg font-bold mt-1" style={{ color: COLORS.navyBlue }}>
                            ${item.subtotal.toFixed(2)}
                          </p>
                        </div>
                        <div className="flex flex-col items-end justify-between">
                          <button 
                            onClick={() => removeItem(item.product_id, item.branch_id)}
                            className="p-1 text-slate-400 hover:text-red-500"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                          <div className="flex items-center border rounded-lg">
                            <button 
                              onClick={() => updateQuantity(item.product_id, item.branch_id, item.quantity - 1)}
                              className="p-2 hover:bg-slate-100"
                            >
                              <Minus className="w-3 h-3" />
                            </button>
                            <span className="w-8 text-center text-sm">{item.quantity}</span>
                            <button 
                              onClick={() => updateQuantity(item.product_id, item.branch_id, item.quantity + 1)}
                              className="p-2 hover:bg-slate-100"
                            >
                              <Plus className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl p-4 sticky top-20">
                <h3 className="font-semibold text-slate-800 mb-4">Resumen del pedido</h3>
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-slate-600">
                    <span>Subtotal ({cart.items.length} productos)</span>
                    <span>${cart.total.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-slate-600">
                    <span>Envío</span>
                    <span className="text-slate-500 italic">Por definir</span>
                  </div>
                </div>
                <div className="border-t pt-4 mb-4">
                  <div className="flex justify-between text-lg font-bold">
                    <span>Total</span>
                    <span style={{ color: COLORS.navyBlue }}>${cart.total.toFixed(2)}</span>
                  </div>
                </div>
                <Link
                  to="/checkout"
                  className="block w-full py-3 rounded-xl text-white font-semibold text-center"
                  style={{ backgroundColor: COLORS.navyBlue }}
                >
                  Continuar al pago
                </Link>
              </div>
            </div>
          </div>
        )}
      </main>

      <MarketplaceFooter />
      <MobileBottomNav />
    </div>
  );
};

// ==================== CHECKOUT PAGE ====================
export const CheckoutPage = () => {
  const navigate = useNavigate();
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [storeSettings, setStoreSettings] = useState({
    delivery_enabled: true,
    pickup_enabled: true,
    cash_enabled: true,
    card_enabled: true
  });
  
  // Saved addresses
  const [savedAddresses, setSavedAddresses] = useState([]);

  // New states for delivery validation
  const [deliveryValidation, setDeliveryValidation] = useState(null);
  const [validatingDelivery, setValidatingDelivery] = useState(false);
  const [alternativeBranches, setAlternativeBranches] = useState([]);
  const [currentBranchId, setCurrentBranchId] = useState(null);
  
  const [form, setForm] = useState({
    delivery_type: 'pickup',
    payment_method: 'cash_on_pickup',
    customer_name: '',
    customer_phone: '',
    delivery_address: '',
    delivery_references: '',
    delivery_coords: null,
    notes: ''
  });

  const customerToken = localStorage.getItem('customer_token');

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!customerToken) {
      navigate('/auth?redirect=/checkout');
      return;
    }
    // Check email verification
    try {
      const customerData = JSON.parse(localStorage.getItem('customer_data') || '{}');
      if (!customerData.email_verified) {
        toast.error('Debes verificar tu correo antes de comprar');
        navigate('/auth?redirect=/checkout');
        return;
      }
    } catch {
      // If parse fails, continue to loadData which will handle auth
    }
    loadData();
  }, [customerToken]);

  const loadData = async () => {
    try {
      setLoading(true);
      const cartResponse = await api.get(`/marketplace/cart?customer_token=${customerToken}`);
      setCart(cartResponse.data);
      
      if (cartResponse.data.items.length === 0) {
        navigate('/carrito');
        return;
      }

      // Get store settings from first item's business_id and save branch_id
      const firstItem = cartResponse.data.items[0];
      if (firstItem?.branch_id) {
        setCurrentBranchId(firstItem.branch_id);
      }
      
      if (firstItem?.business_id) {
        try {
          const storeResponse = await api.get(`/marketplace/stores/${firstItem.business_id}`);
          if (storeResponse.data?.store) {
            const store = storeResponse.data.store;
            const paymentMethods = store.payment_methods || ['cash', 'card'];
            
            const newSettings = {
              delivery_enabled: store.delivery_enabled ?? false,
              pickup_enabled: store.pickup_enabled ?? true,
              cash_enabled: paymentMethods.includes('cash'),
              card_enabled: paymentMethods.includes('card')
            };
            
            setStoreSettings(newSettings);
            
            // Set default delivery type based on store settings
            let defaultDeliveryType = 'pickup';
            let defaultPaymentMethod = 'cash_on_pickup';
            
            if (!newSettings.pickup_enabled && newSettings.delivery_enabled) {
              defaultDeliveryType = 'delivery';
              defaultPaymentMethod = newSettings.cash_enabled ? 'cash_on_delivery' : 'card_on_delivery';
            } else if (newSettings.pickup_enabled) {
              defaultDeliveryType = 'pickup';
              defaultPaymentMethod = newSettings.cash_enabled ? 'cash_on_pickup' : 'card_on_pickup';
            }
            
            setForm(f => ({...f, delivery_type: defaultDeliveryType, payment_method: defaultPaymentMethod}));
          }
        } catch (e) {
          console.log('Could not load store settings');
        }
      }

      // Get customer info from session
      const sessionData = JSON.parse(localStorage.getItem('customer_data') || '{}');
      setCustomer(sessionData);
      setForm(f => ({
        ...f,
        customer_name: sessionData.full_name || '',
        customer_phone: sessionData.phone || '',
        delivery_address: sessionData.default_address || ''
      }));

      // Load saved addresses
      try {
        const profileRes = await api.get(`/marketplace/customer/profile?customer_token=${customerToken}`);
        const addrs = profileRes.data.saved_addresses || [];
        setSavedAddresses(addrs);
      } catch (e) {
        console.log('Could not load saved addresses');
      }
    } catch (error) {
      console.error('Error loading data:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('customer_token');
        navigate('/auth?redirect=/checkout');
      }
    } finally {
      setLoading(false);
    }
  };

  const useCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Tu navegador no soporta geolocalización');
      return;
    }

    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const coords = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        
        // Try to get address from coordinates using reverse geocoding
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${coords.lat}&lon=${coords.lng}&addressdetails=1`
          );
          const data = await response.json();
          
          let address = '';
          if (data.address) {
            const parts = [];
            if (data.address.road) parts.push(data.address.road);
            if (data.address.house_number) parts.push(data.address.house_number);
            if (data.address.suburb || data.address.neighbourhood) {
              parts.push(data.address.suburb || data.address.neighbourhood);
            }
            if (data.address.city || data.address.town) {
              parts.push(data.address.city || data.address.town);
            }
            address = parts.join(', ');
          }

          setForm(f => ({
            ...f,
            delivery_address: address || `Ubicación: ${coords.lat.toFixed(6)}, ${coords.lng.toFixed(6)}`,
            delivery_coords: coords
          }));
          
          toast.success('Ubicación obtenida correctamente');
        } catch (error) {
          // If reverse geocoding fails, just save coordinates
          setForm(f => ({
            ...f,
            delivery_address: `Ubicación: ${coords.lat.toFixed(6)}, ${coords.lng.toFixed(6)}`,
            delivery_coords: coords
          }));
          toast.success('Coordenadas guardadas');
        }
        
        setGettingLocation(false);
      },
      (error) => {
        setGettingLocation(false);
        switch(error.code) {
          case error.PERMISSION_DENIED:
            toast.error('Permiso de ubicación denegado');
            break;
          case error.POSITION_UNAVAILABLE:
            toast.error('Ubicación no disponible');
            break;
          case error.TIMEOUT:
            toast.error('Tiempo de espera agotado');
            break;
          default:
            toast.error('Error al obtener ubicación');
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  // Validate delivery distance when coords change
  const validateDeliveryDistance = async (coords) => {
    if (!currentBranchId || !coords) return;
    
    setValidatingDelivery(true);
    setDeliveryValidation(null);
    setAlternativeBranches([]);
    
    try {
      // Validate delivery for current branch
      const response = await api.post(`/branches/${currentBranchId}/validate-delivery`, {
        lat: coords.lat,
        lng: coords.lng
      });
      
      setDeliveryValidation(response.data);
      
      // If delivery is not available, fetch alternative branches
      if (!response.data.delivery?.available) {
        try {
          const altResponse = await api.get(
            `/branches/${currentBranchId}/nearby-alternatives?lat=${coords.lat}&lng=${coords.lng}`
          );
          setAlternativeBranches(altResponse.data.alternatives || []);
        } catch (e) {
          console.log('Could not fetch alternatives');
        }
      }
    } catch (error) {
      console.error('Error validating delivery:', error);
      // Don't block checkout, just show warning
      setDeliveryValidation({
        delivery: { available: true },
        warning: 'No se pudo validar la distancia'
      });
    } finally {
      setValidatingDelivery(false);
    }
  };

  // Effect to validate delivery when coords change
  useEffect(() => {
    if (form.delivery_type === 'delivery' && form.delivery_coords) {
      validateDeliveryDistance(form.delivery_coords);
    }
  }, [form.delivery_coords, form.delivery_type, currentBranchId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.customer_name || !form.customer_phone) {
      toast.error('Completa los datos de contacto');
      return;
    }

    if (form.delivery_type === 'delivery' && !form.delivery_address && !form.delivery_coords) {
      toast.error('Ingresa la dirección de entrega o usa tu ubicación actual');
      return;
    }

    try {
      setSubmitting(true);
      
      // Build the request with coordinates if available
      const orderData = {
        ...form,
        delivery_location: form.delivery_coords ? {
          lat: form.delivery_coords.lat,
          lng: form.delivery_coords.lng,
          address: form.delivery_address,
          references: form.delivery_references
        } : null
      };
      
      const response = await api.post(`/marketplace/orders?customer_token=${customerToken}`, orderData);
      
      toast.success('¡Pedido realizado con éxito!');
      navigate('/mis-pedidos', { state: { newOrder: response.data.orders[0] } });
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al procesar el pedido'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <MarketplaceHeader />
        <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
          {[1,2,3].map(i => (
            <div key={i} className="bg-white rounded-xl p-6 animate-pulse">
              <div className="h-5 bg-slate-200 rounded w-40 mb-4" />
              <div className="space-y-3">
                <div className="h-10 bg-slate-200 rounded" />
                <div className="h-10 bg-slate-200 rounded" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const hasDeliveryOption = storeSettings.delivery_enabled;
  const hasPickupOption = storeSettings.pickup_enabled;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <MarketplaceHeader />

      {/* Page title bar */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-slate-100 rounded-full sm:hidden">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="font-semibold text-lg ml-2">Finalizar compra</h1>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 py-6 flex-1 w-full">
        <form onSubmit={handleSubmit} className="grid lg:grid-cols-3 gap-6">
          {/* Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Contact Info */}
            <div className="bg-white rounded-xl p-6">
              <h2 className="font-semibold text-slate-800 mb-4">Datos de contacto</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Nombre</label>
                  <input
                    type="text"
                    required
                    value={form.customer_name}
                    onChange={(e) => setForm({...form, customer_name: e.target.value})}
                    className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Teléfono</label>
                  <input
                    type="tel"
                    required
                    value={form.customer_phone}
                    onChange={(e) => setForm({...form, customer_phone: e.target.value})}
                    className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-400"
                  />
                </div>
              </div>
            </div>

            {/* Delivery Type */}
            <div className="bg-white rounded-xl p-6">
              <h2 className="font-semibold text-slate-800 mb-4">Método de entrega</h2>
              
              {/* Warning if no options available */}
              {!hasDeliveryOption && !hasPickupOption && (
                <div className="flex items-center gap-2 p-4 bg-yellow-50 rounded-lg text-yellow-800 mb-4">
                  <AlertCircle className="w-5 h-5" />
                  <span>Este comercio no tiene métodos de entrega configurados.</span>
                </div>
              )}
              
              <div className="space-y-3">
                {/* Pickup Option */}
                {hasPickupOption && (
                  <label className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                    form.delivery_type === 'pickup' ? 'border-orange-400 bg-orange-50' : 'border-slate-200'
                  }`}>
                    <input
                      type="radio"
                      name="delivery_type"
                      value="pickup"
                      checked={form.delivery_type === 'pickup'}
                      onChange={(e) => setForm({...form, delivery_type: e.target.value, payment_method: 'cash_on_pickup'})}
                      className="sr-only"
                    />
                    <Store className="w-6 h-6 text-orange-500" />
                    <div>
                      <p className="font-medium text-slate-800">Recoger en tienda</p>
                      <p className="text-sm text-slate-500">Sin costo adicional</p>
                    </div>
                    {form.delivery_type === 'pickup' && (
                      <Check className="w-5 h-5 text-orange-500 ml-auto" />
                    )}
                  </label>
                )}

                {/* Delivery Option */}
                {hasDeliveryOption && (
                  <label className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                    form.delivery_type === 'delivery' ? 'border-orange-400 bg-orange-50' : 'border-slate-200'
                  }`}>
                    <input
                      type="radio"
                      name="delivery_type"
                      value="delivery"
                      checked={form.delivery_type === 'delivery'}
                      onChange={(e) => setForm({...form, delivery_type: e.target.value, payment_method: 'cash_on_delivery'})}
                      className="sr-only"
                    />
                    <Truck className="w-6 h-6 text-green-500" />
                    <div>
                      <p className="font-medium text-slate-800">Entrega a domicilio</p>
                      <p className="text-sm text-slate-500">El costo se calculará al confirmar</p>
                    </div>
                    {(form.delivery_type === 'delivery' || form.delivery_type === 'en_local') && (
                      <Check className="w-5 h-5 text-orange-500 ml-auto" />
                    )}
                  </label>
                )}
                
                {/* enLocal Delivery Option */}
                <label className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                  form.delivery_type === 'en_local' ? 'border-orange-400 bg-orange-50' : 'border-slate-200'
                }`}>
                  <input
                    type="radio"
                    name="delivery_type"
                    value="en_local"
                    checked={form.delivery_type === 'en_local'}
                    onChange={(e) => setForm({...form, delivery_type: e.target.value, payment_method: 'cash_on_delivery'})}
                    className="sr-only"
                  />
                  <Truck className="w-6 h-6 text-teal-600" />
                  <div>
                    <p className="font-medium text-slate-800">Envío por enLocal</p>
                    <p className="text-sm text-slate-500">Repartidor independiente enLocal</p>
                  </div>
                  {form.delivery_type === 'en_local' && (
                    <Check className="w-5 h-5 text-orange-500 ml-auto" />
                  )}
                </label>

                {/* No delivery available message */}
                {!hasDeliveryOption && hasPickupOption && (
                  <p className="text-sm text-slate-500 italic">Este comercio solo ofrece recolección en tienda.</p>
                )}
              </div>

              {/* Delivery Address Section */}
              {(form.delivery_type === 'delivery' || form.delivery_type === 'en_local') && (
                <div className="mt-4 space-y-4">
                  {/* Saved Addresses */}
                  {savedAddresses.length > 0 && !form.delivery_coords && (
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Direcciones guardadas</label>
                      <div className="space-y-2">
                        {savedAddresses.map(addr => (
                          <button
                            key={addr.id}
                            type="button"
                            onClick={() => {
                              setForm(f => ({
                                ...f,
                                delivery_address: addr.address || '',
                                delivery_references: addr.references || '',
                                delivery_coords: addr.location || null
                              }));
                              if (addr.location) {
                                toast.success('Dirección cargada con ubicación GPS');
                              } else {
                                toast.success('Dirección cargada');
                              }
                            }}
                            className="w-full flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:border-orange-300 hover:bg-orange-50 transition-colors text-left"
                          >
                            <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                              {addr.label === 'Casa' ? <Store className="w-4 h-4 text-slate-500" /> :
                               addr.label === 'Oficina' ? <Package className="w-4 h-4 text-slate-500" /> :
                               <MapPin className="w-4 h-4 text-slate-500" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-slate-800 flex items-center gap-1.5">
                                {addr.label}
                                {addr.is_default && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700 font-semibold">Predeterminada</span>}
                                {addr.location && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 font-semibold">GPS</span>}
                              </p>
                              <p className="text-xs text-slate-500 truncate">{addr.address}</p>
                            </div>
                            <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                          </button>
                        ))}
                      </div>
                      <div className="relative my-3">
                        <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200"></div></div>
                        <div className="relative flex justify-center"><span className="bg-white px-3 text-xs text-slate-400">o elige una nueva ubicación</span></div>
                      </div>
                    </div>
                  )}

                  {/* Use Current Location Button */}
                  {!form.delivery_coords && (
                    <button
                      type="button"
                      onClick={useCurrentLocation}
                      disabled={gettingLocation}
                      className="w-full flex items-center justify-center gap-2 p-3 rounded-lg border-2 border-dashed border-slate-300 hover:border-blue-400 hover:bg-blue-50 transition-colors text-slate-600 hover:text-blue-600"
                    >
                      {gettingLocation ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          <span>Obteniendo ubicación...</span>
                        </>
                      ) : (
                        <>
                          <Navigation className="w-5 h-5" />
                          <span>Usar mi ubicación actual</span>
                        </>
                      )}
                    </button>
                  )}
                  
                  {/* Interactive Map for Location Selection */}
                  {form.delivery_coords && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-green-700 text-sm">
                          <MapPin className="w-4 h-4" />
                          <span>Ubicación confirmada</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => setForm({...form, delivery_coords: null, delivery_address: ''})}
                          className="text-xs text-slate-500 hover:text-red-500"
                        >
                          Cambiar ubicación
                        </button>
                      </div>
                      
                      <DeliveryLocationMap 
                        coords={form.delivery_coords}
                        onCoordsChange={(newCoords, address) => {
                          setForm(f => ({
                            ...f,
                            delivery_coords: newCoords,
                            delivery_address: address || f.delivery_address
                          }));
                        }}
                      />
                      
                      {/* Delivery Validation Status */}
                      {validatingDelivery && (
                        <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg text-blue-700 text-sm">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>Verificando cobertura de entrega...</span>
                        </div>
                      )}
                      
                      {/* Delivery Available */}
                      {!validatingDelivery && deliveryValidation?.delivery?.available && (
                        <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                          <div className="flex items-center gap-2 text-green-700">
                            <Check className="w-5 h-5" />
                            <span className="font-medium">¡Entrega disponible!</span>
                          </div>
                          <div className="mt-1 text-sm text-green-600">
                            <span>Distancia: {deliveryValidation.distance_km} km</span>
                            {deliveryValidation.delivery?.fee !== undefined && (
                              <span className="ml-2">• Costo de envío: ${deliveryValidation.delivery.fee.toFixed(2)}</span>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Delivery NOT Available - Out of Range */}
                      {!validatingDelivery && deliveryValidation?.delivery?.available === false && (
                        <div className="space-y-3">
                          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                            <div className="flex items-start gap-3">
                              <MapPinOff className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                              <div>
                                <p className="font-medium text-amber-800">Fuera del área de cobertura</p>
                                <p className="text-sm text-amber-700 mt-1">
                                  {deliveryValidation.delivery?.message || 'Esta sucursal no puede entregar a tu ubicación.'}
                                </p>
                                <p className="text-sm text-amber-600 mt-1">
                                  Distancia: {deliveryValidation.distance_km} km (máx: {deliveryValidation.delivery?.max_radius_km} km)
                                </p>
                              </div>
                            </div>
                          </div>
                          
                          {/* Alternative Branches */}
                          {alternativeBranches.length > 0 && (
                            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                              <div className="flex items-center gap-2 text-blue-800 mb-3">
                                <Info className="w-5 h-5" />
                                <span className="font-medium">Sucursales que sí pueden entregar:</span>
                              </div>
                              <div className="space-y-2">
                                {alternativeBranches.map((branch) => (
                                  <div 
                                    key={branch.branch_id}
                                    className="flex items-center justify-between p-3 bg-white rounded-lg border border-blue-100"
                                  >
                                    <div>
                                      <p className="font-medium text-slate-800">{branch.branch_name}</p>
                                      <p className="text-xs text-slate-500">{branch.address}</p>
                                      <div className="flex items-center gap-2 mt-1">
                                        <span className="text-xs text-slate-600">{branch.distance_km} km</span>
                                        <span className="text-xs text-green-600">Envío: ${branch.delivery_fee.toFixed(2)}</span>
                                        <span className={`text-xs px-1.5 py-0.5 rounded ${branch.is_open ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                          {branch.is_open ? 'Abierta' : 'Cerrada'}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                              <p className="text-xs text-blue-600 mt-2 italic">
                                Para pedir a otra sucursal, agrega productos desde esa sucursal específica.
                              </p>
                            </div>
                          )}
                          
                          {/* Suggest pickup instead */}
                          {storeSettings.pickup_enabled && (
                            <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                              <p className="text-sm text-slate-700">
                                <strong>Alternativa:</strong> Puedes cambiar a "Recoger en tienda" para continuar con tu pedido.
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Address display (read-only when coords exist) */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      {form.delivery_coords ? 'Dirección detectada' : 'Dirección de entrega'}
                    </label>
                    {form.delivery_coords ? (
                      <div className="p-3 bg-slate-50 border rounded-lg text-slate-700 text-sm">
                        {form.delivery_address || 'Ubicación seleccionada en el mapa'}
                      </div>
                    ) : (
                      <textarea
                        required
                        value={form.delivery_address}
                        onChange={(e) => setForm({...form, delivery_address: e.target.value})}
                        placeholder="Escribe la dirección completa..."
                        rows={2}
                        className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-400 resize-none"
                      />
                    )}
                  </div>
                  
                  {/* References field */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Referencias para el repartidor
                    </label>
                    <textarea
                      value={form.delivery_references}
                      onChange={(e) => setForm({...form, delivery_references: e.target.value})}
                      placeholder="Ej: Casa color azul, entre calle A y calle B, tocar el timbre..."
                      rows={2}
                      className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-400 resize-none"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Payment Method */}
            <div className="bg-white rounded-xl p-6">
              <h2 className="font-semibold text-slate-800 mb-4">Método de pago</h2>
              
              {/* Warning if no payment options available */}
              {!storeSettings.cash_enabled && !storeSettings.card_enabled && (
                <div className="flex items-center gap-2 p-4 bg-yellow-50 rounded-lg text-yellow-800 mb-4">
                  <AlertCircle className="w-5 h-5" />
                  <span>Este comercio no tiene métodos de pago configurados.</span>
                </div>
              )}
              
              <div className="space-y-3">
                {/* Cash Option - Only show if enabled */}
                {storeSettings.cash_enabled && (
                  <label className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                    form.payment_method.includes('cash') ? 'border-orange-400 bg-orange-50' : 'border-slate-200'
                  }`}>
                    <input
                      type="radio"
                      name="payment_method"
                      value={form.delivery_type === 'pickup' ? 'cash_on_pickup' : 'cash_on_delivery'}
                      checked={form.payment_method.includes('cash')}
                      onChange={(e) => setForm({...form, payment_method: e.target.value})}
                      className="sr-only"
                    />
                    <Banknote className="w-6 h-6 text-green-600" />
                    <div>
                      <p className="font-medium text-slate-800">Efectivo</p>
                      <p className="text-sm text-slate-500">
                        {form.delivery_type === 'pickup' ? 'Paga al recoger tu pedido' : 'Paga al recibir tu pedido'}
                      </p>
                    </div>
                    {form.payment_method.includes('cash') && (
                      <Check className="w-5 h-5 text-orange-500 ml-auto" />
                    )}
                  </label>
                )}

                {/* Card Option - Only show if enabled */}
                {storeSettings.card_enabled && (
                  <label className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                    form.payment_method.includes('card') ? 'border-orange-400 bg-orange-50' : 'border-slate-200'
                  }`}>
                    <input
                      type="radio"
                      name="payment_method"
                      value={form.delivery_type === 'pickup' ? 'card_on_pickup' : 'card_on_delivery'}
                      checked={form.payment_method.includes('card')}
                      onChange={(e) => setForm({...form, payment_method: e.target.value})}
                      className="sr-only"
                    />
                    <CreditCard className="w-6 h-6 text-blue-600" />
                    <div>
                      <p className="font-medium text-slate-800">Tarjeta</p>
                      <p className="text-sm text-slate-500">
                        {form.delivery_type === 'pickup' ? 'Paga con tarjeta en la tienda' : 'Paga con tarjeta al recibir'}
                      </p>
                    </div>
                    {form.payment_method.includes('card') && (
                      <Check className="w-5 h-5 text-orange-500 ml-auto" />
                    )}
                  </label>
                )}
                
                {/* Message when only one payment method is available */}
                {storeSettings.cash_enabled && !storeSettings.card_enabled && (
                  <p className="text-sm text-slate-500 italic">Este comercio solo acepta pago en efectivo.</p>
                )}
                {!storeSettings.cash_enabled && storeSettings.card_enabled && (
                  <p className="text-sm text-slate-500 italic">Este comercio solo acepta pago con tarjeta.</p>
                )}
              </div>
            </div>

            {/* Notes */}
            <div className="bg-white rounded-xl p-6">
              <h2 className="font-semibold text-slate-800 mb-4">Notas adicionales (opcional)</h2>
              <textarea
                value={form.notes}
                onChange={(e) => setForm({...form, notes: e.target.value})}
                placeholder="Instrucciones especiales, horario preferido, etc..."
                rows={3}
                className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-400 resize-none"
              />
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl p-4 sticky top-20">
              <h3 className="font-semibold text-slate-800 mb-4">Tu pedido</h3>
              
              <div className="space-y-3 max-h-64 overflow-y-auto mb-4">
                {cart.items.map((item) => (
                  <div key={`${item.product_id}-${item.branch_id}`} className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-slate-100 rounded-lg flex-shrink-0 overflow-hidden">
                      {item.product_image ? (
                        <img src={item.product_image} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Package className="w-5 h-5 text-slate-300" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 line-clamp-1">
                        {item.product_name}
                        {item.is_combo && <span className="ml-1 text-[10px] font-semibold text-purple-600">(Combo)</span>}
                      </p>
                      <p className="text-xs text-slate-500">x{item.quantity}</p>
                      {item.modifier_selections?.length > 0 && (
                        <p className="text-xs text-slate-500 mt-0.5">
                          {item.modifier_selections.flatMap(g => (g.options || []).map(o =>
                            o.price_adjustment > 0 ? `${o.name} (+$${o.price_adjustment.toFixed(2)})` : o.name
                          )).join(', ')}
                        </p>
                      )}
                      {item.combo_selections?.length > 0 && (
                        <p className="text-xs text-purple-500 mt-0.5">
                          {item.combo_selections.map(s => s.product_name).join(' + ')}
                        </p>
                      )}
                      {item.special_instructions && (
                        <p className="text-xs text-amber-600 mt-0.5 italic">{item.special_instructions}</p>
                      )}
                    </div>
                    <p className="text-sm font-medium">${item.subtotal.toFixed(2)}</p>
                  </div>
                ))}
              </div>

              <div className="border-t pt-4 space-y-2 mb-4">
                <div className="flex justify-between text-slate-600">
                  <span>Subtotal</span>
                  <span>${cart.total.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-slate-600">
                  <span>Envío</span>
                  <span>
                    {form.delivery_type === 'pickup' 
                      ? 'Gratis' 
                      : (deliveryValidation?.delivery?.available && deliveryValidation?.delivery?.fee !== undefined)
                        ? `$${deliveryValidation.delivery.fee.toFixed(2)}`
                        : 'Por confirmar'
                    }
                  </span>
                </div>
              </div>

              <div className="border-t pt-4 mb-4">
                <div className="flex justify-between text-lg font-bold">
                  <span>Total</span>
                  <span style={{ color: COLORS.navyBlue }}>
                    ${(cart.total + (form.delivery_type === 'delivery' && deliveryValidation?.delivery?.available ? (deliveryValidation?.delivery?.fee || 0) : 0)).toFixed(2)}
                  </span>
                </div>
              </div>

              {/* Warning when delivery not available */}
              {form.delivery_type === 'delivery' && form.delivery_coords && !validatingDelivery && deliveryValidation?.delivery?.available === false && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                  <AlertCircle className="w-4 h-4 inline mr-1" />
                  No puedes continuar con entrega a domicilio. Cambia a "Recoger en tienda" o selecciona otra ubicación.
                </div>
              )}

              <button
                type="submit"
                disabled={submitting || validatingDelivery || (form.delivery_type === 'delivery' && form.delivery_coords && deliveryValidation?.delivery?.available === false)}
                className="w-full py-3 rounded-xl text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                style={{ backgroundColor: COLORS.navyBlue }}
                data-testid="confirm-order-btn"
              >
                {submitting ? 'Procesando...' : validatingDelivery ? 'Verificando...' : 'Confirmar pedido'}
              </button>

              <p className="text-xs text-slate-500 text-center mt-3">
                Al confirmar, aceptas nuestros <Link to="/terminos" className="underline" style={{ color: COLORS.navyBlue }}>términos y condiciones</Link>
              </p>
            </div>
          </div>
        </form>
      </main>

      <MarketplaceFooter />
      <MobileBottomNav />
    </div>
  );
};

// ==================== MY ORDERS PAGE ====================
export const MyOrdersPage = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const customerToken = localStorage.getItem('customer_token');

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!customerToken) {
      navigate('/auth?redirect=/mis-pedidos');
      return;
    }
    loadOrders();
  }, [customerToken]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/marketplace/orders?customer_token=${customerToken}&page=1&limit=10`);
      const data = response.data;
      setOrders(data.orders || data);
      setHasMore(data.has_more || false);
      setPage(1);
    } catch (error) {
      console.error('Error loading orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    try {
      setLoadingMore(true);
      const nextPage = page + 1;
      const response = await api.get(`/marketplace/orders?customer_token=${customerToken}&page=${nextPage}&limit=10`);
      const data = response.data;
      setOrders(prev => [...prev, ...(data.orders || [])]);
      setHasMore(data.has_more || false);
      setPage(nextPage);
    } catch (error) {
      console.error('Error loading more orders:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-700',
      confirmed: 'bg-blue-100 text-blue-700',
      preparing: 'bg-purple-100 text-purple-700',
      ready: 'bg-green-100 text-green-700',
      delivered: 'bg-green-100 text-green-700',
      cancelled: 'bg-red-100 text-red-700'
    };
    return colors[status] || 'bg-slate-100 text-slate-700';
  };

  const getStatusText = (status) => {
    const texts = {
      pending: 'Pendiente',
      confirmed: 'Confirmado',
      preparing: 'En preparación',
      ready: 'Listo',
      in_transit: 'En camino',
      delivered: 'Entregado',
      cancelled: 'Cancelado'
    };
    return texts[status] || status;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <MarketplaceHeader />
        <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
          {[1,2,3].map(i => (
            <div key={i} className="bg-white rounded-xl p-4 animate-pulse">
              <div className="flex justify-between items-center mb-3">
                <div className="h-4 bg-slate-200 rounded w-28" />
                <div className="h-6 bg-slate-200 rounded-full w-20" />
              </div>
              <div className="space-y-2">
                <div className="h-3 bg-slate-200 rounded w-3/4" />
                <div className="h-3 bg-slate-200 rounded w-1/2" />
              </div>
              <div className="flex justify-between mt-3 pt-3 border-t">
                <div className="h-5 bg-slate-200 rounded w-24" />
                <div className="h-4 bg-slate-200 rounded w-32" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <MarketplaceHeader />

      {/* Page title bar */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center">
          <button onClick={() => navigate('/')} className="p-2 hover:bg-slate-100 rounded-full sm:hidden">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="font-semibold text-lg ml-2">Mis Pedidos</h1>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 py-6 flex-1 w-full">
        {orders.length === 0 ? (
          <div className="text-center py-16">
            <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-700 mb-2">No tienes pedidos</h2>
            <p className="text-slate-500 mb-6">¡Haz tu primer pedido hoy!</p>
            <Link 
              to="/buscar"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-white font-medium"
              style={{ backgroundColor: COLORS.navyBlue }}
            >
              Explorar productos
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map(order => (
              <div key={order.id} className="bg-white rounded-xl overflow-hidden">
                <div className="px-4 py-3 bg-slate-50 border-b flex items-center justify-between">
                  <div>
                    <p className="font-medium text-slate-800">{order.order_number}</p>
                    <p className="text-sm text-slate-500">
                      {new Date(order.created_at).toLocaleDateString('es-MX', {
                        day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                      })}
                    </p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(order.status)}`}>
                    {getStatusText(order.status)}
                  </span>
                </div>

                <div className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Store className="w-4 h-4 text-slate-500" />
                    <span className="text-slate-700">{order.business_name}</span>
                  </div>

                  <div className="space-y-2 mb-4">
                    {order.items.slice(0, 2).map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm">
                        <span className="text-slate-600">{item.quantity}x {item.product_name}</span>
                        <span className="text-slate-800">${item.subtotal.toFixed(2)}</span>
                      </div>
                    ))}
                    {order.items.length > 2 && (
                      <p className="text-sm text-slate-400">+{order.items.length - 2} productos más</p>
                    )}
                  </div>

                  <div className="flex items-center justify-between pt-3 border-t">
                    <span className="font-bold" style={{ color: COLORS.navyBlue }}>
                      Total: ${order.total.toFixed(2)}
                    </span>
                    <span className="text-sm text-slate-500">
                      {order.delivery_type === 'pickup' ? 'Recoger en tienda' : 'Entrega a domicilio'}
                    </span>
                  </div>
                </div>
              </div>
            ))}

            {hasMore && (
              <button
                onClick={loadMore}
                disabled={loadingMore}
                className="w-full py-3 bg-white rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors border flex items-center justify-center gap-2"
              >
                {loadingMore ? (
                  <>
                    <div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
                    Cargando...
                  </>
                ) : (
                  'Cargar más pedidos'
                )}
              </button>
            )}
          </div>
        )}
      </main>

      <MarketplaceFooter />
      <MobileBottomNav />
    </div>
  );
};
